/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.9.1
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
import _Object$defineProperties from 'babel-runtime/core-js/object/define-properties';
import _Promise from 'babel-runtime/core-js/promise';
import _forEach from 'lodash/fp/forEach';
import _stubArray from 'lodash/fp/stubArray';
import _constant from 'lodash/fp/constant';
import _isFunction from 'lodash/fp/isFunction';
import _differenceWith from 'lodash/fp/differenceWith';
import _mapValues from 'lodash/fp/mapValues';
import Vue from 'vue';
import SelectInteraction from 'ol/interaction/select';
import Feature from 'ol/feature';
import { assert, featureHelper, geoJsonHelper, interaction, mergeDescriptors, observableFromOlEvent, styleHelper, stylesContainer } from '../core';

// todo add other options, like event modifiers
var props = {
  filter: {
    type: Function,
    default: _constant(true)
  },
  hitTolerance: {
    type: Number,
    default: 0
  },
  multi: {
    type: Boolean,
    default: false
  },
  /**
   * Selected features as array of GeoJSON features with coordinates in the map view projection.
   * @type {string[]|number[]|GeoJSONFeature[]}
   */
  features: {
    type: Array,
    default: _stubArray
  },
  wrapX: {
    type: Boolean,
    default: true
  }
};

var computed = {};

var methods = {
  /**
   * @return {ol.interaction.Select}
   * @protected
   */
  createInteraction: function createInteraction() {
    return new SelectInteraction({
      multi: this.multi,
      wrapX: this.wrapX,
      filter: this.filter,
      style: this.createStyleFunc()
    });
  },

  /**
   * @return {ol.StyleFunction}
   * @protected
   */
  getDefaultStyles: function getDefaultStyles() {
    var defaultStyles = _mapValues(function (styles) {
      return styles.map(styleHelper.style);
    }, styleHelper.defaultEditStyle());

    return function __selectDefaultStyleFunc(feature) {
      if (feature.getGeometry()) {
        return defaultStyles[feature.getGeometry().getType()];
      }
    };
  },

  /**
   * @return {ol.Feature[]}
   */
  getFeatures: function getFeatures() {
    return this.$interaction && this.$interaction.getFeatures().getArray() || [];
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    return mergeDescriptors(interaction.methods.getServices.call(this), stylesContainer.methods.getServices.call(this));
  },

  /**
   * @return {ol.interaction.Interaction|undefined}
   * @protected
   */
  getStyleTarget: function getStyleTarget() {
    return this.$interaction;
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    interaction.methods.mount.call(this);
    this.features.forEach(this.select);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unselectAll();
    interaction.methods.unmount.call(this);
  },

  /**
   * @param {GeoJSONFeature|Vue|ol.Feature|string|number} feature
   * @return {void}
   * @throws {Error}
   */
  select: function select(feature) {
    assert.hasMap(this);
    assert.hasInteraction(this);

    var id = featureHelper.getId(feature);
    if (!id) {
      throw new Error('Undefined feature id');
    }
    if (feature instanceof Vue) {
      feature = feature.$feature;
    }

    var selectedIds = this.$features.map(featureHelper.getId);
    if (selectedIds.includes(id)) return;

    if (!(feature instanceof Feature)) {
      feature = undefined;
      _forEach(function (layer) {
        var source = layer.getSource();

        if (source && _isFunction(source.getFeatureById)) {
          feature = source.getFeatureById(id);
        }

        return !feature;
      }, this.$map.getLayers().getArray());
    }

    feature && this.$interaction.getFeatures().push(feature);
  },

  /**
   * @param {GeoJSONFeature|Vue|ol.Feature|string|number} feature
   * @return {void}
   */
  unselect: function unselect(feature) {
    assert.hasInteraction(this);

    var id = featureHelper.getId(feature);
    if (!id) {
      throw new Error('Undefined feature id');
    }
    if (feature instanceof Vue) {
      feature = feature.$feature;
    }

    var selectedIds = this.$features.map(featureHelper.getId);
    var idx = selectedIds.findIndex(function (x) {
      return x === id;
    });

    if (idx !== -1) {
      this.$interaction.getFeatures().removeAt(idx);
    }
  },

  /**
   * @param {Array<{style: ol.style.Style, condition: (function|boolean|undefined)}>|ol.StyleFunction|Vue|undefined} styles
   * @return {void}
   * @protected
   */
  setStyle: function setStyle(styles) {
    if (styles !== this._styles) {
      this._styles = styles;
      this.refresh();
    }
  },

  /**
   * @return {Promise}
   */
  refresh: function refresh() {
    var _this = this;

    return _Promise.all([new _Promise(function (resolve) {
      if (_this.$interaction) {
        var featuresCollection = _this.$interaction.getFeatures();
        featuresCollection.once('change', function () {
          return resolve();
        });
        featuresCollection.changed();
      } else {
        resolve();
      }
    }), interaction.methods.refresh.call(this)]);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToInteractionChanges.call(this);
  },

  /**
   * Removes all features from selection.
   * @return {void}
   */
  unselectAll: function unselectAll() {
    assert.hasInteraction(this);

    this.$interaction.getFeatures().clear();
  }
};

var diffById = _differenceWith(function (a, b) {
  return featureHelper.getId(a) === featureHelper.getId(b);
});
var watch = {
  features: function features(value) {
    if (!this.$interaction) return;

    var forSelect = diffById(value, this.$features);
    var forUnselect = diffById(this.$features, value);

    forSelect.forEach(this.select);
    forUnselect.forEach(this.unselect);
  }
};

// TODO: use featuresContainer mixin
var Interaction = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('i', { class: [_vm.$options.name], staticStyle: { "display": "none !important" } }, [_vm._t("default", null, { features: _vm.features })], 2);
  }, staticRenderFns: [],
  name: 'vl-interaction-select',
  mixins: [interaction, stylesContainer],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch,
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        class: this.$options.name
      };
    }
  },
  created: function created() {
    _Object$defineProperties(this, {
      $features: {
        enumerable: true,
        get: this.getFeatures
      }
    });
  }
};

/**
 * @return {void}
 * @private
 */
function subscribeToInteractionChanges() {
  var _this2 = this;

  assert.hasInteraction(this);

  var events = observableFromOlEvent(this.$interaction, 'select');

  this.subscribeTo(events, function (_ref) {
    var selected = _ref.selected,
        deselected = _ref.deselected,
        mapBrowserEvent = _ref.mapBrowserEvent;

    ++_this2.rev;

    deselected.forEach(function (feature) {
      return _this2.$emit('unselect', { feature: feature, mapBrowserEvent: mapBrowserEvent });
    });
    selected.forEach(function (feature) {
      return _this2.$emit('select', { feature: feature, mapBrowserEvent: mapBrowserEvent });
    });
    _this2.$emit('update:features', _this2.$features.map(function (feature) {
      return geoJsonHelper.writeFeature(feature);
    }));
  });
}

var index = {
  Interaction: Interaction,
  install: function install(Vue$$1) {
    Vue$$1.component(Interaction.name, Interaction);
  }
};

export default index;

//# sourceMappingURL=index.es.js.map



//////////////////
// WEBPACK FOOTER
// ./node_modules/vuelayers/lib/select-interaction/index.es.js
// module id = null
// module chunks = 